import { useState } from 'react'
import { Loader2, Send } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { Site, PublishRequest } from '@/types'

interface PublishModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  articleId: string
  articleTitle: string
  sites: Site[]
  onPublish: (articleId: string, data: PublishRequest) => Promise<void>
  isPublishing: boolean
}

export function PublishModal({
  open,
  onOpenChange,
  articleId,
  articleTitle,
  sites,
  onPublish,
  isPublishing,
}: PublishModalProps) {
  const [siteId, setSiteId] = useState<string>(sites[0]?.id || '')
  const [publishStatus, setPublishStatus] = useState<'draft' | 'publish'>('publish')
  const [uploadImage, setUploadImage] = useState(true)

  const handlePublish = async () => {
    if (!siteId) return

    await onPublish(articleId, {
      site_id: siteId,
      publish_status: publishStatus,
      upload_image: uploadImage,
    })
  }

  const activeSites = sites.filter((s) => s.is_active)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Publish to WordPress
          </DialogTitle>
          <DialogDescription>
            Publish "{articleTitle}" to your WordPress site.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {activeSites.length === 0 ? (
            <div className="text-center py-4 text-muted-foreground">
              <p>No active WordPress sites configured.</p>
              <p className="text-sm mt-2">Please add a site in Settings first.</p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <Label htmlFor="site">WordPress Site</Label>
                <Select value={siteId} onValueChange={setSiteId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a site" />
                  </SelectTrigger>
                  <SelectContent>
                    {activeSites.map((site) => (
                      <SelectItem key={site.id} value={site.id}>
                        {site.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="status">Publish Status</Label>
                <Select value={publishStatus} onValueChange={(v) => setPublishStatus(v as 'draft' | 'publish')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="publish">Publish immediately</SelectItem>
                    <SelectItem value="draft">Save as draft</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="uploadImage">Featured Image</Label>
                <Select value={uploadImage ? 'yes' : 'no'} onValueChange={(v) => setUploadImage(v === 'yes')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yes">Upload image to WordPress</SelectItem>
                    <SelectItem value="no">Skip image upload</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPublishing}>
            Cancel
          </Button>
          <Button onClick={handlePublish} disabled={isPublishing || !siteId || activeSites.length === 0}>
            {isPublishing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Publishing...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Publish
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
